import BaseComponent from '../BaseComponent';
import EventHandler from '../../utils/dom/event-handler';
import Manipulator from '../../utils/dom/manipulator';
import SelectorEngine from '../../utils/dom/selector-engine';
import { VALID_POSTCODE_CHECK } from '../../utils/form-validation';
import {
	getPaymentGatewayInstance,
	elementClasses,
	elementStyles
} from '../PaymentMethodSelector/payment-provider-gateway';

export const POSTCODE_STATE_CHANGE_EVENT = 'POSTCODE_STATE_CHANGE';
export const CARD_FIELDS_INITIALISED_EVENT = 'CARD_FIELDS_INITIALISED';
export const CARD_FIELDSET_SELECTOR = 'card-payment-fields';
export const CARD_BILLING_POSTCODE_SELECTOR = 'card-billing-postcode';
export const ACCEPTED_CARD_TYPES = Object.freeze({
	visa: 'Visa',
	mastercard: 'Mastercard',
	amex: 'American Express'
});
const unsupportedCardErrorMessage = `Please use an accepted card type: ${Object.values(ACCEPTED_CARD_TYPES)
	.join(', ')
	.replace(/,([^,]*)$/, ' or$1')}`;

// prettier-ignore
const onCardNumberChange =(cardNumberElementSelector) =>
	({ brand, empty, error }) => {
		// Use our own icons & positioning
		const isUnknownType = !empty && brand === 'unknown';
		const isAcceptedBrandOfCard = Object.keys(ACCEPTED_CARD_TYPES).includes(brand);
		for (const icon of SelectorEngine.find(`${cardNumberElementSelector} .card-type-icon`)) {
			Manipulator.toggleClassCond(icon, 'u-hidden', isAcceptedBrandOfCard && !isUnknownType ? icon.dataset.brand !== brand : false);
		}
		return {
			error: !empty && !isUnknownType && !isAcceptedBrandOfCard ? { message: unsupportedCardErrorMessage } : error
		};
	};

const setValidationAppearance = (wrapperElement, hasError, messageText) => {
	Manipulator.toggleClassCond(wrapperElement, 'is-invalid', hasError);
	// Shouldn't need this 2nd one, but that's how it is
	Manipulator.toggleClassCond(wrapperElement, 'is-focused', hasError);
	const errorMessageElement = SelectorEngine.children(wrapperElement, '.lta-text-input--error')[0];
	if (errorMessageElement && messageText) {
		errorMessageElement.textContent = messageText ? messageText : '';
	}
	Manipulator.toggleClassCond(errorMessageElement, 'u-hidden', !hasError);
};

class PaymentCardFields extends BaseComponent {
	constructor(element) {
		super(element);

		this.provider = Manipulator.getDataAttribute(element, 'provider');

		const paymentGateway = getPaymentGatewayInstance(this.provider);
		if (!paymentGateway) {
			return;
		}

		this.fieldset = SelectorEngine.findOne(`#${CARD_FIELDSET_SELECTOR}`);
		this.elements = paymentGateway.elements;

		this.postCodeInput = SelectorEngine.findOne(`#${CARD_BILLING_POSTCODE_SELECTOR}`);
		this.postCodeState = {
			value: '',
			isTouched: false,
			isInvalid: true
		};

		const cardElements = this.setUpCardElements();
		this.setUpPostCode();

		EventHandler.trigger(this.fieldset, CARD_FIELDS_INITIALISED_EVENT, {
			...cardElements,
			postCodeInput: this.postCodeInput
		});
	}

	static NAME() {
		return 'PaymentCardFields';
	}

	validatePostCode() {
		const postCodeValidator = new RegExp(VALID_POSTCODE_CHECK);
		const value = this.postCodeInput.value.trim();
		const hasValueChanged = this.postCodeState.value !== value;
		const isInvalid = !postCodeValidator.test(value);

		const newState = { isTouched: true, isInvalid, value };

		this.postCodeState = newState;

		if (hasValueChanged || isInvalid) {
			EventHandler.trigger(this.fieldset, POSTCODE_STATE_CHANGE_EVENT, this.postCodeState);
		}

		return newState;
	}

	setUpPostCode() {
		EventHandler.on(this.fieldset, POSTCODE_STATE_CHANGE_EVENT, ({ isInvalid }) => {
			setValidationAppearance(SelectorEngine.parents(this.postCodeInput, '.lta-text-input__wrapper')[0], isInvalid);
		});

		EventHandler.on(this.postCodeInput, 'change', () => {
			const { isInvalid } = this.validatePostCode();
			if (isInvalid) {
				EventHandler.on(this.postCodeInput, 'input', () => this.validatePostCode());
			}
		});
	}

	setUpElement(parentSelector, elementType, elementOptions, changeEvent) {
		if(this.elements.getElement(elementType)) {
			this.elements.getElement(elementType)?.destroy()
		}
		const stripeElement = this.elements.create(elementType, elementOptions);
		stripeElement.mount(`${parentSelector}-element`);

		stripeElement.on('change', (event) => {
			const changeResult = changeEvent ? changeEvent(event) : undefined;
			const errorToShow = (changeResult && changeResult.error) || event.error;

			setValidationAppearance(
				SelectorEngine.findOne(parentSelector),
				!!errorToShow,
				errorToShow ? errorToShow.message : ''
			);
		});

		return stripeElement;
	}

	setUpCardElements() {
		const appearance = { classes: elementClasses, style: elementStyles };
		const cardNumberElementSelector = '#card-number';

		const cardNumber = this.setUpElement(
			cardNumberElementSelector,
			'cardNumber',
			{ ...appearance, showIcon: false },
			onCardNumberChange(cardNumberElementSelector)
		);
		const cardExpiry = this.setUpElement('#card-expiry', 'cardExpiry', appearance);
		const cardCvc = this.setUpElement('#card-cvc', 'cardCvc', appearance);

		return { cardCvc, cardExpiry, cardNumber };
	}

	getElements() {
		return { cardCvc: this.elements.getElement('cardCvc'), cardExpiry: this.elements.getElement('cardExpiry'), cardNumber: this.elements.getElement('cardNumber') };
	}

	destroyElement(elementType) {
		this.elements.getElement(elementType)?.destroy();
	}

	destroy() {
		this.destroyElement('cardNumber');
		this.destroyElement('cardExpiry');
		this.destroyElement('cardCvc');
	}
}

export default PaymentCardFields;
